import { SpinnerMonoIndicators as Spinner } from '@clearkit/icons';
import { CKBox, CKCompanyLogo } from 'clearkit';
import { useState } from 'react';
import { useHistory } from 'react-router';

import { AuthLayoutHeader } from '~/components/layouts/AuthLayout';

import useDiscoveryLogin from '../../shared/useDiscoveryLogin';
import { Account, DiscoveredAccount } from '../types';

type ListOrganizationsProps = {
  accounts: DiscoveredAccount[];
};

export default function ListOrganizations({
  accounts,
}: ListOrganizationsProps) {
  const { mutateAsync, isLoading } = useDiscoveryLogin();
  const [selectedAccount, setSelectedAccount] = useState<Account | null>();

  const history = useHistory();

  async function login(account: Account) {
    if (isLoading) {
      return;
    }

    setSelectedAccount(account);
    try {
      const { isSuccess, error } = await mutateAsync(account.id);

      if (isSuccess) {
        window.location.href = '/';
      } else {
        if (error.action === 'unauthorized') {
          history.push(`/auth/login?error=${error.type}`);
        } else {
          throw new Error(`Unexpected error: ${JSON.stringify(error)}`);
        }
      }
    } finally {
      setSelectedAccount(null);
    }
  }

  const sortedAccounts = [...accounts].sort((a, b) =>
    a.account.name.localeCompare(b.account.name),
  );

  return (
    <>
      <AuthLayoutHeader
        className="sm:w-96"
        description="Pick an account to continue."
        header={
          <div className="w-full font-semibold text-center">
            Choose your Clearbit account
          </div>
        }
      />
      <CKBox className="w-full p-5 bg-white sm:w-[440px]">
        <ul className="flex flex-col gap-6">
          {sortedAccounts.map((discovered, idx) => (
            <li className="flex items-center gap-2" key={idx}>
              <span>
                <CKCompanyLogo
                  size={24}
                  src={`https://logo.clearbit.com/${discovered.account.domain}`}
                />
              </span>
              <span className="flex-grow text-left">
                {discovered.account.name}
              </span>
              {discovered.account === selectedAccount ? (
                <Spinner className="w-6 h-6" />
              ) : (
                <a
                  className="text-sm cursor-pointer"
                  onClick={() => login(discovered.account)}
                >
                  {actionLabel(discovered)}
                </a>
              )}
            </li>
          ))}
        </ul>
      </CKBox>
    </>
  );
}

function actionLabel(discovered: DiscoveredAccount) {
  switch (discovered.membership_type) {
    case 'invited_member':
      return 'Accept invite & Login';
    case 'active_member':
      return 'Login';
    case 'pending_member':
      return 'Join & Login';
    case 'eligible_to_join_by_email_domain':
      return 'Join & Login';
    default:
      return 'Login';
  }
}
